/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from '~src/hooks/useStyles';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { Typography, Button, Avatar, message, Tooltip } from 'antd';
import { claimSharedTender } from '~src/services/api/shares';
import { UserOutlined } from '@ant-design/icons';
import CachedSharesContext from '~src/contexts/CachedSharesContext';
import { Link, useHistory } from 'react-router-dom';

const { Text } = Typography;

export type UserLoggedInProps = {
  shareId?: string | undefined;
  shareKey?: string | undefined | null;
  switchTab?: (string) => void;
  currentUserData?: {
    account_ids: number[];
    email: string;
    id: number;
    is_customer_admin: boolean;
  };
};

export const UserLoggedIn: React.FC<UserLoggedInProps> = ({
  shareId,
  shareKey,
  switchTab,
  currentUserData,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const history = useHistory();
  const { cachedSharesContext, setCachedSharesContext } = useContext(
    CachedSharesContext
  );

  const [
    dbClaimTenderShare,
    { isLoading: isUpdateTenderLoading },
  ] = useMutation(claimSharedTender, {
    onSuccess: () => {
      queryCache.invalidateQueries('shares');
      queryCache.invalidateQueries([QueryKeys.sharedTenderDetails]);

      message.success(
        intl.formatMessage({
          id: 'tender-claim-user-logged-in-claim-success',
        })
      );

      // Remove the claimed share from cached shares list
      if (shareId && shareKey) {
        const updatedCachedShares = cachedSharesContext.filter(
          (share) => !(share.shareId === shareId && share.shareKey === shareKey)
        );

        // Update both the context and localStorage to sync the /invitations list
        setCachedSharesContext(updatedCachedShares);
        localStorage.setItem('shares', JSON.stringify(updatedCachedShares));
      }

      if (shareId && shareKey) {
        // Redirect to /my-tenders/{shareId}-{shareKeyId}
        history.push(`/my-tenders/${shareId}-${shareKey}`);
      } else {
        history.push('/my-tenders');
      }
    },
    onError: (err: any) => {
      const errorMsg =
        err?.response?.data?.message ||
        intl.formatMessage({
          id: 'tender-claim-user-logged-in-claim-error',
        });
      message.error(errorMsg);
    },
  });

  const handleClaimTenderShared = async () => {
    if (!shareId || !shareKey) {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-user-logged-in-missing-share-info',
        })
      );
      return;
    }
    await dbClaimTenderShare({ shareId, shareKey });
  };

  return (
    <div css={styles.formContainer}>
      <Avatar size={80} icon={<UserOutlined rev={null} />} />{' '}
      <div
        css={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
        <span>
          {intl.formatMessage({
            id: 'tender-claim-user-logged-in-claim-as',
          })}
        </span>
        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
          {currentUserData?.email}
        </span>
      </div>
      <div>
        <Button
          onClick={handleClaimTenderShared}
          type="primary"
          size="large"
          style={{ backgroundColor: '#2cbd9b', borderColor: '#2cbd9b' }}
          loading={isUpdateTenderLoading}>
          {intl.formatMessage({
            id: 'tender-claim-user-logged-in-claim-button',
          })}
        </Button>
      </div>
      <div style={{ marginTop: 8, textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
          {intl.formatMessage({
            id: 'tender-claim-user-logged-in-have-another-account',
          })}
        </Text>
        {/* @ts-ignore */}
        <Link onClick={() => switchTab?.('user-login')}>
          {intl.formatMessage({
            id: 'tender-claim-user-logged-in-switch-account',
          })}
        </Link>
      </div>
    </div>
  );
};

const styleFn = () => ({
  formContainer: {
    height: '100%',
    minHeight: 260,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '25px',
  },
});
